import { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import api from '../api/client';

const STATUS_MAP = {
  pending:      ['badge-warning',  'Pending'],
  under_review: ['badge-info',     'Under Review'],
  confirmed:    ['badge-success',  'Confirmed'],
  rejected:     ['badge-danger',   'Rejected'],
  completed:    ['badge-success',  'Completed'],
  cancelled:    ['badge-danger',   'Cancelled'],
};

export default function SdCampRequests() {
  const [requests, setRequests] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [filter, setFilter] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ distributor_id: '', attender_name: '', attender_email: '', attender_phone: '', status: '', notes: '' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get('/camp-requests/sd').then(res => setRequests(res.data.campRequests)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/super-distributors/me/distributors').then(res => setDistributors(res.data.distributors || [])).catch(() => {});
  }, []);

  function openEdit(r) {
    setEditing(r);
    setForm({ distributor_id: r.distributor_id || '', attender_name: r.attender_name || '', attender_email: r.attender_email || '', attender_phone: r.attender_phone || '', status: r.status, notes: r.notes || '' });
    setError('');
  }

  // Assigning a distributor makes them the camp attender by default — pull
  // their own contact details in immediately so the SD doesn't retype them.
  function handleAssignDistributor(distributorId) {
    const dist = distributors.find(d => d.id === distributorId);
    setForm(p => ({
      ...p,
      distributor_id: distributorId,
      attender_name: dist ? dist.name : p.attender_name,
      attender_email: dist ? dist.email : p.attender_email,
      attender_phone: dist ? dist.mobile : p.attender_phone,
    }));
  }

  async function handleSave() {
    setError('');
    setSaving(true);
    try {
      await api.put(`/camp-requests/sd/${editing.id}`, { ...form, distributor_id: form.distributor_id || null });
      setEditing(null);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update');
    } finally { setSaving(false); }
  }

  const visible = filter ? requests.filter(r => r.status === filter) : requests;

  return (
    <Layout role="superDistributor">
      <div className="page-header">
        <div><h1 className="page-title">Camp Requests</h1><p className="page-subtitle">Review camp requests from your schools and distributors</p></div>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
        {['', 'pending', 'under_review', 'confirmed', 'rejected', 'completed', 'cancelled'].map(s => (
          <button key={s} className={`btn btn-sm ${filter === s ? 'btn-primary' : 'btn-outline'}`} onClick={() => setFilter(s)}>
            {s ? STATUS_MAP[s]?.[1] || s : 'All'}
          </button>
        ))}
      </div>

      <div className="card">
        <div className="table-responsive">
          <table className="data-table">
            <thead>
              <tr><th>School</th><th>Distributor</th><th>Camp Name</th><th>Start</th><th>End</th><th>Camp Attender</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {visible.length === 0 ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: 24 }}>No camp requests found.</td></tr>
              ) : visible.map(r => {
                const [badgeCls, label] = STATUS_MAP[r.status] || ['badge-warning', r.status];
                return (
                  <tr key={r.id}>
                    <td style={{ fontWeight: 600 }}>{r.school_name}</td>
                    <td>{r.distributor_name || <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>Direct</span>}</td>
                    <td>{r.camp_name}</td>
                    <td>{r.start_date ? new Date(r.start_date).toLocaleDateString('en-IN') : '—'}</td>
                    <td>{r.end_date ? new Date(r.end_date).toLocaleDateString('en-IN') : '—'}</td>
                    <td>{r.attender_name || <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>Not set</span>}</td>
                    <td><span className={`badge ${badgeCls}`}>{label}</span></td>
                    <td>
                      <button className="btn btn-sm btn-outline" onClick={() => openEdit(r)}>
                        <i className="fas fa-edit" style={{ marginRight: 4 }}></i>Update
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <div className="modal-overlay show" style={{ display: 'flex' }} onClick={() => setEditing(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3><i className="fas fa-edit" style={{ color: 'var(--primary)', marginRight: 8 }}></i>Update Camp Request</h3>
              <button className="modal-close" onClick={() => setEditing(null)}>×</button>
            </div>
            <div className="modal-body">
              {error && <div style={{ background: '#FEE2E2', color: 'var(--danger)', padding: 10, borderRadius: 8, fontSize: 13, marginBottom: 14 }}>{error}</div>}
              <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 13 }}>
                <strong>{editing.camp_name}</strong> — {editing.school_name}
              </div>
              <div className="form-group">
                <label className="form-label">Assign Distributor</label>
                <select className="form-select" value={form.distributor_id} onChange={e => handleAssignDistributor(e.target.value)}>
                  <option value="">-- Not Assigned --</option>
                  {distributors.map(d => (
                    <option key={d.id} value={d.id}>{d.name}{d.area_of_operation ? ` (${d.area_of_operation})` : d.city ? ` (${d.city})` : ''}</option>
                  ))}
                </select>
                <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6 }}>
                  Assigning a distributor lets them see and serve this camp, and fills the Camp Attender fields below with their contact details automatically.
                </p>
              </div>
              <div className="form-row form-row-3">
                <F label="Attender Name" value={form.attender_name} onChange={v => setForm(p => ({ ...p, attender_name: v }))} />
                <F label="Attender Email" value={form.attender_email} onChange={v => setForm(p => ({ ...p, attender_email: v }))} />
                <F label="Attender Phone" value={form.attender_phone} onChange={v => setForm(p => ({ ...p, attender_phone: v }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Mark as Under Review</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.status === 'under_review'} onChange={e => setForm(p => ({ ...p, status: e.target.checked ? 'under_review' : p.status }))} />
                  Set status to "Under Review"
                </label>
                <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6 }}>
                  Final confirmation is done by the Super Admin once the camp is scheduled.
                </p>
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-control" rows={2} value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setEditing(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

function F({ label, value, onChange }) {
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      <input type="text" className="form-control" value={value || ''} onChange={e => onChange(e.target.value)} />
    </div>
  );
}
